import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cortexApi, isApiConfigured, type HealthResponse } from '../../services/cortexApi';
import { isApiClientError } from '@cortex/domain/client';

/**
 * Compact status pill that reports whether `@cortex/api` is reachable.
 *
 * Polls `GET /healthz` on mount and every `pollIntervalMs` (default 60s).
 * Falls back to a neutral "API not configured" state when
 * `VITE_API_BASE_URL` is unset — the SPA is then driving everything
 * locally and the indicator should not light up red just because
 * there's nothing to call.
 *
 * Design constraint: the indicator must never throw — a faulty health
 * probe must not crash the workbench. Every error path lands in a
 * status update, never a re-thrown exception.
 */

type Status = 'unconfigured' | 'unknown' | 'checking' | 'up' | 'down';

interface ApiStatusIndicatorProps {
  /** Visible-text mode. Collapsed → dot-only with tooltip. */
  readonly expanded?: boolean;
  /** Override polling cadence. Pass 0 to disable polling. */
  readonly pollIntervalMs?: number;
}

const DEFAULT_POLL_MS = 60_000;

interface StatusVisual {
  readonly dot: string;
  readonly label: string;
  readonly tone: string;
}

const VISUAL: Record<Status, StatusVisual> = {
  unconfigured: {
    dot: 'bg-slate-500',
    label: 'API not configured',
    tone: 'text-slate-400',
  },
  unknown: {
    dot: 'bg-slate-500 animate-pulse',
    label: 'API status pending',
    tone: 'text-slate-400',
  },
  checking: {
    dot: 'bg-amber-400 animate-pulse',
    label: 'Checking API…',
    tone: 'text-amber-300',
  },
  up: {
    dot: 'bg-emerald-400',
    label: 'API healthy',
    tone: 'text-emerald-300',
  },
  down: {
    dot: 'bg-red-500',
    label: 'API unreachable',
    tone: 'text-red-300',
  },
};

export const ApiStatusIndicator: React.FC<ApiStatusIndicatorProps> = ({
  expanded = true,
  pollIntervalMs = DEFAULT_POLL_MS,
}) => {
  const configured = isApiConfigured();
  const [status, setStatus] = useState<Status>(configured ? 'unknown' : 'unconfigured');
  const [version, setVersion] = useState<HealthResponse['version'] | null>(null);
  const [lastErrorCode, setLastErrorCode] = useState<string | null>(null);
  const inFlightAbort = useRef<AbortController | null>(null);

  const probe = useCallback(async () => {
    if (!configured) return;
    inFlightAbort.current?.abort();
    const controller = new AbortController();
    inFlightAbort.current = controller;
    setStatus((prev) => (prev === 'up' ? 'up' : 'checking'));
    try {
      const result = await cortexApi.health.check({ signal: controller.signal });
      if (controller.signal.aborted) return;
      setVersion(result.version);
      setLastErrorCode(null);
      setStatus('up');
    } catch (err) {
      if (controller.signal.aborted) return;
      const code = isApiClientError(err) ? err.code : 'unknown_error';
      setLastErrorCode(code);
      setStatus('down');
    }
  }, [configured]);

  useEffect(() => {
    if (!configured) return;
    void probe();
    if (pollIntervalMs <= 0) return;
    const id = setInterval(() => {
      void probe();
    }, pollIntervalMs);
    return () => {
      clearInterval(id);
      inFlightAbort.current?.abort();
    };
  }, [configured, pollIntervalMs, probe]);

  const visual = VISUAL[status];
  const tooltip = (() => {
    if (status === 'unconfigured') return 'Set VITE_API_BASE_URL to enable.';
    if (status === 'up' && version) return `API healthy — ${version}`;
    if (status === 'down' && lastErrorCode) return `API unreachable — ${lastErrorCode}`;
    return visual.label;
  })();

  return (
    <div
      className="group relative flex items-center gap-2"
      role="status"
      aria-live="polite"
      aria-label={tooltip}
    >
      <span className={`h-2 w-2 rounded-full shrink-0 ${visual.dot}`} aria-hidden="true" />
      {expanded ? (
        <span className={`text-micro uppercase tracking-widest font-bold truncate ${visual.tone}`}>
          {visual.label}
        </span>
      ) : (
        <span className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-white text-micro rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-[60] font-bold uppercase tracking-wider shadow-xl border border-white/10">
          {tooltip}
        </span>
      )}
    </div>
  );
};
